import React, { useEffect, useRef } from "react";
import gsap from "gsap";

const skipMotion = () =>
  typeof window !== "undefined" &&
  (navigator.webdriver || // Puppeteer render runs — show content instantly
    window.matchMedia("(prefers-reduced-motion: reduce)").matches);

/**
 * Reveals children (staggered) when the wrapper scrolls into view.
 * Children are selected via the `data-reveal` attribute; if none are
 * marked, the wrapper itself is animated.
 *
 * Uses IntersectionObserver (not ScrollTrigger) so layout shifts from
 * lazy-loaded content can never break the trigger, plus a hard timeout
 * failsafe — content can NEVER stay invisible.
 */
const Reveal = ({ children, className = "", y = 40, stagger = 0.12 }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || skipMotion()) return;

    const marked = el.querySelectorAll("[data-reveal]");
    const targets = marked.length ? [...marked] : [el];

    gsap.set(targets, { opacity: 0, y });

    let revealed = false;
    const show = () => {
      if (revealed) return;
      revealed = true;
      gsap.to(targets, {
        opacity: 1,
        y: 0,
        duration: 0.9,
        ease: "power3.out",
        stagger,
        overwrite: true,
      });
    };

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          show();
          io.disconnect();
        }
      },
      { rootMargin: "0px 0px -10% 0px" }
    );
    io.observe(el);

    // Absolute failsafe: whatever happens, content becomes visible
    const failsafe = setTimeout(show, 4000);

    return () => {
      io.disconnect();
      clearTimeout(failsafe);
      gsap.set(targets, { clearProps: "opacity,transform" });
    };
  }, [y, stagger]);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
};

export default Reveal;
